import { AlertModal, Loader } from '@/components/Common';
import { Input } from '@/components/ui';
import { CACHE_TIME } from '@/constants';
import { trpc } from '@/data-loaders';
import { useHandleError, useHandleSuccess } from '@/hooks';
import { useAwmsStore } from '@/store';
import { Button } from '@curtin-dxp/curtin-ui';
import { Search, Trash2 } from 'lucide-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

type HdrAllocationRow = {
  academicWorkloadId: number;
  studentCurtinId: string;
  studentFirstName: string;
  studentLastName: string;
  staffFirstName: string;
  staffLastName: string;
  eftsl: string;
  supervisorRole: string;
  unitCode: string;
};

export const HdrAllocations = () => {
  const [enteredStudentId, setEnteredStudentId] = useState<string>('');
  const [studentIdToQuery, setStudentIdToQuery] = useState<string>('');
  const [retrieveClickCount, setRetrieveClickCount] = useState<number>(0);
  const [rowToDelete, setRowToDelete] = useState<HdrAllocationRow | null>(null);
  const activeYear = useAwmsStore((state) => state.activeYear);
  const sendToastRemoveHdrSuccess = useHandleSuccess({ title: 'Remove HDR allocation' });
  const handleError = useHandleError();
  const lastErrorRef = useRef<unknown>(null);
  const trpcUtils = trpc.useUtils();

  const {
    data: allocations,
    refetch,
    isFetched,
    error: getAllocationsError,
  } = trpc.admin.getHdrAllocationsByStudentId.useQuery(
    { studentCurtinId: studentIdToQuery, year: activeYear },
    {
      cacheTime: CACHE_TIME,
      enabled: !!studentIdToQuery,
      retry: false,
    },
  );

  const { isLoading: isRemoving, mutate: removeHdrMutation } = trpc.admin.removeHdrStudentWorkload.useMutation({
    onSuccess: async () => {
      sendToastRemoveHdrSuccess();
      await trpcUtils.admin.getHdrAllocationsByStudentId.invalidate();
    },
    onError: (error) => {
      handleError([error]);
    },
  });

  useEffect(() => {
    if (studentIdToQuery) refetch();
  }, [retrieveClickCount, studentIdToQuery, refetch]);

  useEffect(() => {
    if (getAllocationsError && lastErrorRef.current !== getAllocationsError) {
      handleError([getAllocationsError]);
      lastErrorRef.current = getAllocationsError;
    }
  }, [handleError, getAllocationsError]);

  const handleStudentIdChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setStudentIdToQuery('');
    setEnteredStudentId(event.target.value);
  };

  const handleRetrieveClick = () => {
    setStudentIdToQuery(enteredStudentId);
    setRetrieveClickCount(retrieveClickCount + 1);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete) {
      removeHdrMutation({
        academicWorkloadId: rowToDelete.academicWorkloadId,
        studentCurtinId: rowToDelete.studentCurtinId,
        unitCode: rowToDelete.unitCode,
        year: activeYear,
      });
    }
    setRowToDelete(null);
  };

  if (isRemoving) {
    return <Loader />;
  }

  const hasResults = !!allocations && allocations.length > 0;
  const showEmptyState = !!studentIdToQuery && isFetched && allocations?.length === 0;

  return (
    <div>
      <AlertModal
        title="Do you want to remove this HDR allocation?"
        description="This will permanently delete the HDR student workload allocation."
        confirmAction={handleConfirmDelete}
        open={!!rowToDelete}
        cancelAction={() => setRowToDelete(null)}
      />
      <div className="ml-8 mt-10">
        <div className="flex w-[500px] justify-between">
          <div className="relative">
            <Input
              type="text"
              value={enteredStudentId}
              className="w-[180px] border-secondary-grey-04 pr-10 text-b2"
              onChange={handleStudentIdChange}
              placeholder="Enter student ID"
            />
          </div>
          <Button
            variant="outline-secondary"
            className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
              focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
            aria-label="Retrieve HDR allocations"
            onClick={handleRetrieveClick}
          >
            Retrieve HDR allocations
            <Search className="ml-[10px] h-6 w-6" />
          </Button>
        </div>
        {hasResults && (
          <div className="mt-4">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead>
                <tr>
                  <th className="border border-gray-300 px-4 py-2">Student ID</th>
                  <th className="border border-gray-300 px-4 py-2">Student name</th>
                  <th className="border border-gray-300 px-4 py-2">Staff name</th>
                  <th className="border border-gray-300 px-4 py-2">EFTSL</th>
                  <th className="border border-gray-300 px-4 py-2">Role</th>
                  <th className="border border-gray-300 px-4 py-2">Remove</th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((row) => (
                  <tr key={`${row.academicWorkloadId}-${row.unitCode}`} className="border-t">
                    <td className="border-2 px-4 py-2">{row.studentCurtinId}</td>
                    <td className="border-2 px-4 py-2">
                      {row.studentLastName}, {row.studentFirstName}
                    </td>
                    <td className="border-2 px-4 py-2">
                      {row.staffLastName}, {row.staffFirstName}
                    </td>
                    <td className="border-2 px-4 py-2">{row.eftsl}</td>
                    <td className="border-2 px-4 py-2">{row.supervisorRole}</td>
                    <td className="border-2 px-4 py-2 text-center">
                      <button onClick={() => setRowToDelete(row)} aria-label="Remove HDR allocation">
                        <Trash2 className="stroke-secondary-grey-02 hover:stroke-deep-blue" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {showEmptyState && (
          <div className="mt-4 text-base text-secondary-grey-02">
            No HDR allocations found for this student.
          </div>
        )}
      </div>
    </div>
  );
};
